import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Typography } from '@mui/material';
import { useApiGet } from '../../utils/httpClientTs';
import { getConversations } from '../../endpoints/messagesTs';
import { Conversation, SortOrder, ConversationListResponse } from '../../types';
import { ServerSideTableColumn } from '../../Components/ServerSideTable';
import ServerSideTable from '../../Components/ServerSideTable';
import CircleIcon from '@mui/icons-material/Circle';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

dayjs.extend(utc);
dayjs.extend(timezone);

export default function Conversations() {
  const navigate = useNavigate();
  const userId = localStorage.getItem('user_id');

  // Pagination and sorting state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [order, setOrder] = useState<SortOrder>('desc');
  const [orderBy, setOrderBy] = useState<keyof Conversation>('last_message_at');

  const { data: conversationsData, isFetching } =
    useApiGet<ConversationListResponse>(
      ['conversations', page, rowsPerPage, orderBy, order],
      getConversations,
      {
        enabled: userId !== null,
        retry: 1,
        refetchInterval: 5 * 60 * 1000, // Refetch every 5 minutes
      },
      {
        page: page + 1, // API uses 1-based pagination
        per_page: rowsPerPage,
        sort: orderBy,
        order: order,
      }
    );

  const handlePageChange = useCallback((newPage: number) => {
    setPage(newPage);
  }, []);

  const handleRowsPerPageChange = useCallback((newRowsPerPage: number) => {
    setRowsPerPage(newRowsPerPage);
    setPage(0); // Reset to first page when changing page size
  }, []);

  const handleSort = useCallback(
    (property: keyof Conversation) => {
      const isAsc = orderBy === property && order === 'asc';
      setOrder(isAsc ? 'desc' : 'asc');
      setOrderBy(property);
    },
    [orderBy, order]
  );

  const handleRowClick = useCallback(
    (conversation: Conversation) => {
      navigate(`/conversations/${conversation.id}`);
    },
    [navigate]
  );

  const columns: ServerSideTableColumn<Conversation>[] = [
    {
      id: 'is_read',
      label: '',
      width: '50px',
      firstCell: true,
      sortable: false,
      render: (value: boolean) => (
        <Box display="flex" justifyContent="center" alignItems="center">
          {!value && <CircleIcon color="primary" sx={{ fontSize: 10 }} />}
        </Box>
      ),
    },
    {
      id: 'title',
      label: 'Title',
      width: '30%',
      sortable: true,
      render: (value: string, row: Conversation) => (
        <Typography
          variant="body1"
          fontWeight={row.is_read ? 'normal' : 'bold'}
          color="primary"
          sx={{ cursor: 'pointer' }}
        >
          {value}
        </Typography>
      ),
    },
    {
      id: 'last_message',
      label: 'Last Message',
      width: '45%',
      sortable: false,
      render: (value: string) => (
        <Typography variant="body2" sx={{ fontSize: 14 }}>
          {value}
        </Typography>
      ),
    },
    {
      id: 'last_message_at',
      label: 'Date',
      width: '20%',
      sortable: true,
      render: (value: string) => (
        <Typography variant="caption">
          {dayjs.utc(value).local().format('M/DD/YYYY h:mm A')}
        </Typography>
      ),
    },
  ];

  if (isFetching && !conversationsData) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography>Loading...</Typography>
      </Box>
    );
  }

  if (!conversationsData || conversationsData.data.length === 0) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h4" component="h1" sx={{ mb: 1 }}>
          Messages
        </Typography>
        <Typography>No conversations found.</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" component="h1" sx={{ mb: 3 }}>
        Messages
      </Typography>

      <ServerSideTable
        data={conversationsData.data}
        columns={columns}
        loading={isFetching}
        page={page}
        rowsPerPage={rowsPerPage}
        totalCount={conversationsData.pagination.total_count}
        totalPages={conversationsData.pagination.total_pages}
        hasNext={conversationsData.pagination.has_next}
        hasPrev={conversationsData.pagination.has_prev}
        order={order}
        orderBy={orderBy}
        onPageChange={handlePageChange}
        onRowsPerPageChange={handleRowsPerPageChange}
        onSort={handleSort}
        onRowClick={handleRowClick}
        emptyMessage="No conversations found."
      />
    </Box>
  );
}
